import {
  Cell,
  type LegendPayload,
  type LegendProps,
  Pie,
  PieChart,
  type PieLabelRenderProps,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import type { BrowserBreakdown } from '@/types/analytics'

interface BrowsersChartProps {
  data: BrowserBreakdown[]
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
]

const chartConfig = {
  chrome: {
    label: 'Chrome',
    color: COLORS[0],
  },
  safari: {
    label: 'Safari',
    color: COLORS[1],
  },
  firefox: {
    label: 'Firefox',
    color: COLORS[2],
  },
  edge: {
    label: 'Edge',
    color: COLORS[3],
  },
  other: {
    label: 'Other',
    color: COLORS[4],
  },
} satisfies ChartConfig

const chartClassNames = {
  card: 'flex-1 rounded-2xl shadow-sm',
  header: 'pb-2',
  title: 'text-base font-semibold leading-4',
  subtitle: 'text-sm text-muted-foreground',
  content: 'pt-0',
}

// recharts 3's PieLabelRenderProps no longer exposes our custom `percentage`
// data field directly; it lives on the entry `payload`. Read it from there so
// the label keeps rendering the exact same 0-100 value (not the 0..1 `percent`).
function readPercentage(payload: unknown): number | undefined {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'percentage' in payload
  ) {
    const { percentage } = payload
    if (typeof percentage === 'number') {
      return percentage
    }
  }
  return undefined
}

export function BrowsersChart({ data }: BrowsersChartProps) {
  // Map data to include colors
  const chartData = data.map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length],
  }))

  return (
    <Card className={chartClassNames.card}>
      <CardHeader className={chartClassNames.header}>
        <CardTitle className={chartClassNames.title}>Browsers</CardTitle>
        <p className={chartClassNames.subtitle}>
          Percent of conversations by browser type
        </p>
      </CardHeader>
      <CardContent className={chartClassNames.content}>
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <PieChart accessibilityLayer>
            <ChartTooltip content={<ChartTooltipContent />} />
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="browser"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ payload }: PieLabelRenderProps) => {
                const percentage = readPercentage(payload)
                return percentage === undefined
                  ? ''
                  : `${percentage.toFixed(0)}%`
              }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <ChartLegend
              content={({
                payload,
                verticalAlign,
              }: {
                payload?: ReadonlyArray<LegendPayload>
                verticalAlign?: LegendProps['verticalAlign']
              }) => (
                <ChartLegendContent
                  payload={payload ? [...payload] : []}
                  verticalAlign={verticalAlign}
                  nameKey="browser"
                />
              )}
            />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
